/**
 * User Repository Interface
 * Defines the contract for user data access
 */

export class IUserRepository {
  /**
   * Get all users
   * @param {{viewerUserId?: string}} options - Optional viewer context for server-side visibility scoping
   * @returns {Promise<User[]>}
   */
  async getAllUsers(options = {}) {
    throw new Error('getAllUsers must be implemented');
  }

  /**
   * Get user by ID
   * @param {string} userId - User ID
   * @returns {Promise<User|null>}
   */
  async getUserById(userId) {
    throw new Error('getUserById must be implemented');
  }

  /**
   * Get user by email
   * @param {string} email - User email
   * @returns {Promise<User|null>}
   */
  async getUserByEmail(email) {
    throw new Error('getUserByEmail must be implemented');
  }

  /**
   * Create user
   * @param {Object} userData - User data
   * @param {{sendNotificationEmail?: boolean}} options - Optional behavior flags
   * @returns {Promise<User>}
   */
  async createUser(userData, options = {}) {
    throw new Error('createUser must be implemented');
  }

  /**
   * Update user
   * @param {string} userId - User ID
   * @param {Object} updates - User updates
   * @returns {Promise<User>}
   */
  async updateUser(userId, updates) {
    throw new Error('updateUser must be implemented');
  }

  /**
   * Delete user
   * @param {string} userId - User ID
   * @returns {Promise<boolean>}
   */
  async deleteUser(userId) {
    throw new Error('deleteUser must be implemented');
  }

  /**
   * Get users enrolled in a course
   * @param {string} courseId - Course ID
   * @returns {Promise<User[]>}
   */
  async getUsersByCourse(courseId) {
    throw new Error('getUsersByCourse must be implemented');
  }

  /**
   * Enroll user in course
   * @param {string} userId - User ID
   * @param {string} courseId - Course ID
   * @param {{sendNotificationEmail?: boolean}} options - Optional behavior flags
   * @returns {Promise<User>}
   */
  async enrollUserInCourse(userId, courseId, options = {}) {
    throw new Error('enrollUserInCourse must be implemented');
  }

  /**
   * Unenroll user from course
   * @param {string} userId - User ID
   * @param {string} courseId - Course ID
   * @returns {Promise<User>}
   */
  async unenrollUserFromCourse(userId, courseId) {
    throw new Error('unenrollUserFromCourse must be implemented');
  }

  /**
   * Suspend a student in a specific course
   * @param {string} userId - User ID
   * @param {string} courseId - Course ID
   * @returns {Promise<User>}
   */
  async suspendUserInCourse(userId, courseId) {
    throw new Error('suspendUserInCourse must be implemented');
  }

  /**
   * Unsuspend a student in a specific course
   * @param {string} userId - User ID
   * @param {string} courseId - Course ID
   * @returns {Promise<User>}
   */
  async unsuspendUserInCourse(userId, courseId) {
    throw new Error('unsuspendUserInCourse must be implemented');
  }

  /**
   * Create the first admin account (bootstrap)
   * Must only succeed when no users exist
   * @param {Object} adminData - Initial admin data
   * @returns {Promise<User>}
   */
  async createInitialAdmin(adminData) {
    throw new Error('createInitialAdmin must be implemented');
  }

  /**
   * Set or reset user password
   * @param {string} userId - User ID
   * @param {string} password - New plaintext password
    * @param {{courseId?: string, resetUrl?: string, sendNotificationEmail?: boolean}} options - Optional email template context and send behavior
   * @returns {Promise<User>}
   */
  async setUserPassword(userId, password, options = {}) {
    throw new Error('setUserPassword must be implemented');
  }

  /**
   * Request a self-service password reset email.
   * @param {string} email - User email
   * @param {{courseId?: string}} options - Optional course context for template selection
   * @returns {Promise<{success: boolean, message: string}>}
   */
  async requestPasswordReset(email, options = {}) {
    throw new Error('requestPasswordReset must be implemented');
  }

  /**
   * Complete a self-service password reset using a one-time token.
   * @param {string} token - Reset token
   * @param {string} password - New plaintext password
   * @returns {Promise<{success: boolean}>}
   */
  async resetPasswordWithToken(token, password) {
    throw new Error('resetPasswordWithToken must be implemented');
  }

  /**
   * Sign in or register via a Google ID token.
   * If a user with the same email already exists it is linked to the Google identity.
   * @param {string} idToken - Google credential JWT
   * @returns {Promise<User>}
   */
  async loginWithGoogle(idToken) {
    throw new Error('loginWithGoogle must be implemented');
  }
}
